import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { SessionLoader } from '@/components';
import {
  Navigate,
  Route,
  RouteProps,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useRoleSwitch } from '@/hooks/useRoleSwitch';
import { ArrowRightLeft } from 'lucide-react';
import {
  About,
  AdvisorClientAccessConflictPage,
  AdvisorConsent,
  AdvisorDashboard,
  AdvisorOnboarding,
  AdvisorPublicInvite,
  AuthSignout,
  ClientHome,
  ClientPolicyApplication,
  ConsentDisclosurePage,
  DocusignCompletionPage,
  DocusignEmbeddedSigningPage,
  AutoSignInLink,
  EmailLinkSigninVerification,
  EmailLinkSignupVerification,
  EmailPreferences,
  Login,
  NotFound,
  ClerkSignup,
  CcrcTypeAProductPage,
  OnboardingCarePhasesPhaseOne,
  OnboardingCarePhasesSummary,
  OnboardingCareSettingsPhaseOne,
  OnboardingCareSettingsPhaseThree,
  OnboardingCareSettingsPhaseTwo,
  OnboardingCostFactors,
  OnboardingInsightsSummary,
  OnboardingDownloadReport,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFundingCoverage,
  OnboardingFundingSources,
  OnboardingOurPredictions,
  RateActionLetterPage,
  OnboardingPdfReport,
  OnboardingPolicyApplicationDashboard,
  OnboardingPredictedCareNeeds,
  OnboardingResultsAreIn,
  OnboardingOverview,
  PolicyApplicationFormPage,
  RecordConsentsPage,
  RedirectHome,
  RedirectStripeCheckout,
  SeatInvitationResponse,
  Specs,
  AdvisorRedirect,
  WizardAppConfigFeatureFlags,
  WizardDataDump,
  WizardEvalsPage,
  WizardFundingCalcPromptPage,
  WizardUnderwritingGuidelinesPage,
  WizardUnderwritingGuidelineEditorPage,
  OnboardingRedirect,
  OnboardingFormPage,
  FormBuilderPage,
  ShopInsurancePage,
  IllustrationMonitorPage,
  IllustrationMonitorAvailabilityPage,
  IllustrationMonitorRequestsPage,
  OnboardingD2CPaywall,
  OnboardingD2CVirtualMeeting,
  OnboardingD2CPaymentSuccess,
  D2CCheckoutPage,
  SignIn,
} from '@/pages';
import { ClientRoute } from '@/components/ClientRoute';
import { UnauthorizedPage } from '@/components/UnauthorizedPage';
import { GoogleOAuthCallback } from '@/pages/GoogleOauthCallback';
import { DuplicateAccountScreen } from '@/pages/DuplicateAccountScreen';
import { MicrosoftOAuthCallback } from '@/pages/MicrosoftOauthCallback';
import { WealthboxOAuthCallback } from '@/pages/WealthboxOAuthCallback';
import { SalesforceOAuthCallback } from '@/pages/SalesforceOAuthCallback';
import { OnboardingPdfReportPrint } from '@/pages/OnboardingPdfReportPrint';
import { LtcFinancialPlanning } from '@/pages/ltc-financial-planning/LtcFinancialPlanning';
import { GuidedFinancialPlanningPage } from '@/features/guided-financial-planning';
import { EditAssumptionsPage } from '@/features/edit-assumptions';
import { ProjectionDetailsPage } from '@/features/projection-details';
import {
  GenworthDashboard,
  GenworthOnboardingOverview,
  GenworthOnboardingResultsAreIn,
  GenworthOnboardingOurPredictions,
  GenworthOnboardingPredictedCareNeeds,
  GenworthOnboardingCarePhasesPhaseOne,
  GenworthOnboardingCareSettingsPhaseOne,
  GenworthOnboardingCareSettingsPhaseTwo,
  GenworthOnboardingCareSettingsPhaseThree,
  useIsGenworthMode,
} from '@/features/genworth';
import { GENWORTH_POLICY_EDUCATION_SLIDE_DEFS } from '@/features/genworth/education/policy-education-slides';
import { LoginRedirect } from '@/components/LoginRedirect';

import { useClerkClientAuth } from '@/hooks/useClerkClientAuth';
import { useSelector } from 'react-redux';
import { isPublicOnboardingPath } from '@/hooks/useClientRoutes';

import {
  ExError,
  SessionType,
  allSessionTypes,
  assertSameArrays,
  regexExtractCaptures,
} from '@shared';
import { selectSession } from '@client/model/selectors';
import { Button } from '@design-system';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

export type PageFn<TProps extends ObjectMap<string> | {} = {}> = FunctionComponent<TProps>;

export type QueryProps = { query: string };

type AppRouteSource = [PageFn, string];

export interface AppRoute {
  pageFn: PageFn;
  path: string;
  paramNames: string[];
  allowedSessionTypes: SessionType[];
}

export const unauthenticatedAppRoutes = (
  [
    [SignIn, '/'],
    [EmailLinkSigninVerification, '/sign-in/verify'],
    [EmailLinkSignupVerification, '/sign-up/verify'],
    // Must be public: the recipient arrives signed out, and SessionLoader would
    // otherwise bounce them before the sign-in token can be redeemed. Prefix
    // matching means the '/auth/login' entry below does not cover this path.
    [AutoSignInLink, '/auth/link'],
    [ClerkSignup, '/signup'],
    [RedirectHome, '/auth/login'],
    [Specs, '/specs'],
    [EmailPreferences, '/email/preferences'],
    [AuthSignout, '/auth/signout'],
    [GoogleOAuthCallback, '/oauth/google/callback'],
    [MicrosoftOAuthCallback, '/oauth/microsoft/callback'],
    [WealthboxOAuthCallback, '/integrations/oauth/callback'],
    [SalesforceOAuthCallback, '/integrations/salesforce/oauth/callback'],
    [SeatInvitationResponse, '/public/seat-invitations/:subscriptionSeatId/:invitedEmail/:action'],
    [SeatInvitationResponse, '/public/seat-invitations/:subscriptionSeatId/:action'],
    [AdvisorPublicInvite, '/onboard/:advisorId'],
  ] as AppRouteSource[]
).map(([pageFn, path]) => createAppRoute(pageFn, path, []));

export const authenticatedAppRoutes = (
  [
    [OnboardingOverview, '/clients/:clientId/onboarding/overview'],
    [OnboardingResultsAreIn, '/clients/:clientId/onboarding/results-are-in'],
    [OnboardingOurPredictions, '/clients/:clientId/onboarding/our-predictions'],
    [OnboardingInsightsSummary, '/clients/:clientId/onboarding/insights-summary'],
    [OnboardingD2CPaywall, '/clients/:clientId/onboarding/d2c-paywall'],
    [OnboardingD2CVirtualMeeting, '/clients/:clientId/onboarding/d2c-virtual-meeting'],
    [OnboardingD2CPaymentSuccess, '/clients/:clientId/onboarding/d2c-payment-success'],
    [OnboardingCostFactors, '/clients/:clientId/onboarding/cost-factors'],
    [OnboardingCarePhasesPhaseOne, '/clients/:clientId/onboarding/care-phases-early'],
    [OnboardingCareSettingsPhaseOne, '/clients/:clientId/onboarding/care-settings-phase-one'],
    [OnboardingCareSettingsPhaseTwo, '/clients/:clientId/onboarding/care-settings-phase-two'],
    [OnboardingCareSettingsPhaseThree, '/clients/:clientId/onboarding/care-settings-phase-three'],
    [
      OnboardingFamilyCaregivingPhaseOne,
      '/clients/:clientId/onboarding/family-caregiving-phase-one',
    ],
    [
      OnboardingFamilyCaregivingPhaseTwo,
      '/clients/:clientId/onboarding/family-caregiving-phase-two',
    ],
    [
      OnboardingFamilyCaregivingPhaseThree,
      '/clients/:clientId/onboarding/family-caregiving-phase-three',
    ],
    [OnboardingPredictedCareNeeds, '/clients/:clientId/onboarding/predicted-care-needs'],
    [OnboardingCarePhasesSummary, '/clients/:clientId/onboarding/care-phases-summary'],
    [OnboardingFundingCoverage, '/clients/:clientId/onboarding/funding-coverage'],
    [OnboardingFundingSources, '/clients/:clientId/onboarding/funding-sources'],
    [RateActionLetterPage, '/clients/:clientId/rate-action-letter'],
    [ShopInsurancePage, '/clients/:clientId/onboarding/shop-insurance'],
    [CcrcTypeAProductPage, '/clients/:clientId/onboarding/ccrc-type-a'],
    [D2CCheckoutPage, '/clients/:clientId/onboarding/d2c-checkout'],
    [OnboardingDownloadReport, '/clients/:clientId/onboarding/download-report'],
    [OnboardingPdfReport, '/clients/:clientId/onboarding/report'],
    [OnboardingPdfReportPrint, '/clients/:clientId/onboarding/report/print'],
    [
      OnboardingPolicyApplicationDashboard,
      '/clients/:clientId/onboarding/policy-application-dashboard',
    ],
    [
      DocusignEmbeddedSigningPage,
      '/clients/:clientId/embedded-signing/:clientPolicyApplicationId/:policyApplicationFormId',
    ],
    [DocusignCompletionPage, '/docusign/completion'],
    [ClientPolicyApplication, '/clients/:clientId/policy-application/:clientPolicyApplicationId'],
    [
      PolicyApplicationFormPage,
      '/clients/:clientId/policy-form/:clientPolicyApplicationId/:formId/:forClientId',
    ],
    [RecordConsentsPage, '/clients/:clientId/record-consents'],
    // LTC Financial Planning route (single route with query params)
    [LtcFinancialPlanning, '/clients/:clientId/onboarding/ltc-financial-planning'],
    // Guided Financial Planning route (nested routes for wizard steps)
    [GuidedFinancialPlanningPage, '/clients/:clientId/onboarding/guided-planning/*'],
    // Edit Assumptions page (standalone settings page)
    [EditAssumptionsPage, '/clients/:clientId/onboarding/edit-assumptions'],
    // Ledger Inspector — dev/whitebox view of the monthly claim ledger
    [ProjectionDetailsPage, '/clients/:clientId/projection-details'],
    [ProjectionDetailsPage, '/clients/:clientId/projection-details/:policyId'],
    [OnboardingFormPage, '/onboarding-form/:formReferenceId'],
    [ConsentDisclosurePage, '/consent-disclosure'],
  ] as AppRouteSource[]
).map(([pageFn, path]) =>
  createAppRoute(pageFn, path, [SessionType.advisor, SessionType.wizard, SessionType.client]),
);

export const authenticatedAdvisorOnlyAppRoutes = (
  [
    [RedirectStripeCheckout, '/auth/stripe-checkout'],
    [AdvisorRedirect, '/advisors/redirect/*'],
    [AdvisorConsent, '/advisors/:advisorId/consent'],
    [AdvisorDashboard, '/advisors/:advisorId'],
    [AdvisorDashboard, '/advisors'],
    [AdvisorDashboard, '/advisors/:advisorId/*'],
    [AdvisorOnboarding, '/advisors/:advisorId/onboarding/*'],
    [About, '/about'],
  ] as AppRouteSource[]
).map(([pageFn, path]) => createAppRoute(pageFn, path, [SessionType.advisor, SessionType.wizard]));

export const wizardAppRoutes = (
  [
    [WizardDataDump, '/wizard/data-dump/clients/:clientId'],
    [WizardEvalsPage, '/wizard/evals'],
    [WizardUnderwritingGuidelinesPage, '/wizard/underwriting-guidelines'],
    [WizardUnderwritingGuidelineEditorPage, '/wizard/underwriting-guidelines/:promptKey'],
    [FormBuilderPage, '/wizard/form-builder/*'],
    [WizardAppConfigFeatureFlags, '/wizard/app-config-feature-flags'],
    [WizardFundingCalcPromptPage, '/wizard/funding-calc-prompt'],
    [IllustrationMonitorPage, '/wizard/illustration-finder'],
    [IllustrationMonitorAvailabilityPage, '/wizard/illustration-finder/availability'],
    [IllustrationMonitorRequestsPage, '/wizard/illustration-finder/logs'],
  ] as AppRouteSource[]
).map(([pageFn, path]) => createAppRoute(pageFn, path, [SessionType.wizard]));

export const appRoutes = [
  ...unauthenticatedAppRoutes,
  createAppRoute(OnboardingRedirect, '/onboarding/:formReferenceId', [
    SessionType.unauthenticated,
    SessionType.unknown,
    SessionType.client,
    SessionType.advisor,
  ]),
  ...authenticatedAppRoutes,
  ...wizardAppRoutes,
  ...authenticatedAdvisorOnlyAppRoutes,
  createAppRoute(ClientHome, '/client/home', [
    SessionType.client,
    SessionType.unknown,
    SessionType.unauthenticated,
  ]),
  createAppRoute(AdvisorClientAccessConflictPage, '/advisor-client-conflict', allSessionTypes),
  createAppRoute(DuplicateAccountScreen, '/duplicate-account', [SessionType.duplicateAccount]),
];

// Genworth parallel app layer (gated by the `genworthMode` advisor feature flag).
// Mirrors the advisor-only routes but renders the Genworth-configured dashboard shell.
export const genworthAdvisorOnlyAppRoutes = (
  [
    [RedirectStripeCheckout, '/auth/stripe-checkout'],
    [AdvisorRedirect, '/advisors/redirect/*'],
    [AdvisorConsent, '/advisors/:advisorId/consent'],
    [GenworthDashboard, '/advisors/:advisorId'],
    [GenworthDashboard, '/advisors'],
    [GenworthDashboard, '/advisors/:advisorId/*'],
    [AdvisorOnboarding, '/advisors/:advisorId/onboarding/*'],
    [About, '/about'],
  ] as AppRouteSource[]
).map(([pageFn, path]) => createAppRoute(pageFn, path, [SessionType.advisor, SessionType.wizard]));

// Genworth-specific copies of client-onboarding pages, keyed by the standard page they
// replace. Each copy reuses the standard page's slide identity, so the onboarding
// navigation/progress is unaffected — only the rendered page differs. Add a page here to
// fork it for Genworth.
const GENWORTH_PAGE_SWAPS = new Map<PageFn, PageFn>([
  [OnboardingOverview as PageFn, GenworthOnboardingOverview as PageFn],
  [OnboardingResultsAreIn as PageFn, GenworthOnboardingResultsAreIn as PageFn],
  [OnboardingOurPredictions as PageFn, GenworthOnboardingOurPredictions as PageFn],
  [OnboardingPredictedCareNeeds as PageFn, GenworthOnboardingPredictedCareNeeds as PageFn],
  [OnboardingCarePhasesPhaseOne as PageFn, GenworthOnboardingCarePhasesPhaseOne as PageFn],
  [OnboardingCareSettingsPhaseOne as PageFn, GenworthOnboardingCareSettingsPhaseOne as PageFn],
  [OnboardingCareSettingsPhaseTwo as PageFn, GenworthOnboardingCareSettingsPhaseTwo as PageFn],
  [OnboardingCareSettingsPhaseThree as PageFn, GenworthOnboardingCareSettingsPhaseThree as PageFn],
]);

// Client-onboarding routes with the Genworth page copies swapped in (same paths).
export const genworthAuthenticatedAppRoutes = authenticatedAppRoutes.map(route => {
  const genworthPage = GENWORTH_PAGE_SWAPS.get(route.pageFn);
  return genworthPage ? { ...route, pageFn: genworthPage } : route;
});

// Genworth-only policy-education slides (new pages inserted into the onboarding flow).
export const genworthEducationAppRoutes = GENWORTH_POLICY_EDUCATION_SLIDE_DEFS.map(def =>
  createAppRoute(def.component as PageFn, def.path, [
    SessionType.advisor,
    SessionType.wizard,
    SessionType.client,
  ]),
);

// Full route set rendered when Genworth mode is enabled: identical to `appRoutes` except the
// advisor dashboard and Genworth-specific onboarding pages are swapped in.
export const genworthAppRouteSet = [
  ...unauthenticatedAppRoutes,
  createAppRoute(OnboardingRedirect, '/onboarding/:formReferenceId', [
    SessionType.unauthenticated,
    SessionType.unknown,
    SessionType.client,
    SessionType.advisor,
  ]),
  ...genworthAuthenticatedAppRoutes,
  ...genworthEducationAppRoutes,
  ...wizardAppRoutes,
  ...genworthAdvisorOnlyAppRoutes,
  createAppRoute(ClientHome, '/client/home', [
    SessionType.client,
    SessionType.unknown,
    SessionType.unauthenticated,
  ]),
  createAppRoute(AdvisorClientAccessConflictPage, '/advisor-client-conflict', allSessionTypes),
  createAppRoute(DuplicateAccountScreen, '/duplicate-account', [SessionType.duplicateAccount]),
];

export const appRoutesByPage = new Map<PageFn, AppRoute>();
rebuildAppRoutesByPage();

const ProtectedRoute = ({
  component,
  allowedSessionTypes,
}: {
  component: PageFn;
  allowedSessionTypes: SessionType[];
}) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { isClerkSignedIn } = useClerkClientAuth();
  const session = useSelector(selectSession);
  const { sessionType, forceRedirect } = session;
  const Component = component;
  const pathname = window.location.pathname;
  const { flags } = useFeatureFlags();

  if (!isClerkSignedIn) {
    return <LoginRedirect />;
  }

  const isAdvisorSession = sessionType === SessionType.advisor;
  const isWizardSession = sessionType === SessionType.wizard;
  const advisorId = session.advisor?.advisorId;

  // Skip advisor-client conflict redirect when advisorClientLogin featur-flag is on
  // Handle advisor-client conflict (advisor sessions only)
  if (
    !flags.advisorClientLogin &&
    isAdvisorSession &&
    pathname === '/client/home' &&
    searchParams.get('client_sign_in') === 'true'
  ) {
    const currentSearch = window.location.search;
    return (
      <Navigate
        to={`/advisor-client-conflict${currentSearch}`}
        replace
      />
    );
  }

  // Server-computed force redirect for advisor and wizard sessions
  // Handles: onboarding, seat status, terms of service, and profile completeness checks
  // forceRedirect is a relative path (e.g., '/onboarding/basic-info', '/seats', '/consent', '/settings')
  const isOnboardingFormPage = pathname.startsWith('/onboarding-form/');
  if (
    (isAdvisorSession || isWizardSession) &&
    advisorId &&
    forceRedirect &&
    !isOnboardingFormPage
  ) {
    const fullRedirectPath = `/advisors/${advisorId}${forceRedirect}`;
    const advisorBasePath = `/advisors/${advisorId}`;

    // Check if user is already on the target path to avoid redirect loops
    // For onboarding, allow any path under /onboarding/ (user navigates between steps)
    // For other redirects (seats, consent), use exact match or nested path check
    const isOnboardingRedirect = forceRedirect.startsWith('/onboarding/');
    const isAlreadyOnPath = isOnboardingRedirect
      ? pathname.startsWith(`${advisorBasePath}/onboarding/`)
      : pathname === fullRedirectPath || pathname.startsWith(`${fullRedirectPath}/`);

    if (!isAlreadyOnPath) {
      return (
        <Navigate
          to={fullRedirectPath}
          replace
        />
      );
    }
  }

  // Redirect from base advisor path to clients dashboard (advisor and wizard sessions)
  if ((isAdvisorSession || isWizardSession) && advisorId) {
    const isBasePath =
      !!pathname.match(/^\/advisors\/[a-z0-9]{4}-[a-z0-9]{3}$/) ||
      pathname === '/advisors' ||
      pathname === '/advisors/';

    if (isBasePath) {
      return (
        <Navigate
          to={`/advisors/${advisorId}/clients`}
          replace
        />
      );
    }
  }

  // Wizards bypass remaining application state checks
  if (isWizardSession) {
    if (!allowedSessionTypes.includes(sessionType) && isClerkSignedIn) {
      return <UnauthorizedPage />;
    }
    return <Component {...params} />;
  }

  if (!allowedSessionTypes.includes(sessionType) && isClerkSignedIn) {
    return <UnauthorizedPage />;
  }

  return <Component {...params} />;
};

export function Router() {
  const isGenworthMode = useIsGenworthMode();
  const routes = isGenworthMode ? genworthAppRouteSet : appRoutes;

  return (
    <SessionLoader>
      <Routes>
        {routes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={<RouteProxy {...route} />}
          />
        ))}
        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
    </SessionLoader>
  );
}

function ClientViewMode() {
  const { toggleRole } = useRoleSwitch();
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!expanded) return;

    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    if (!isTouch) return;

    timerRef.current = setTimeout(() => setExpanded(false), 3000);
    return () => void (timerRef.current && clearTimeout(timerRef.current));
  }, [expanded]);

  if (localStorage.getItem('waterlily-role-override') !== 'client') return null;

  const pill = expanded
    ? 'min-w-[280px] rounded-b-[22px] px-5 pb-3 pt-2.5'
    : 'min-w-[120px] rounded-b-[18px] px-4 pb-1.5 pt-1';
  const hoverPill =
    'group-hover:min-w-[280px] group-hover:rounded-b-[22px] group-hover:px-5 group-hover:pb-3 group-hover:pt-2.5';
  const transition = 'transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]';

  return (
    <div
      className="group fixed left-1/2 top-0 z-50 -translate-x-1/2"
      onClick={() => setExpanded(v => !v)}
    >
      <div
        className={`relative flex cursor-pointer items-center justify-center border border-white/10 bg-black/40 text-white shadow-[0_4px_24px_rgba(0,0,0,0.2)] backdrop-blur-xl ${transition} ${pill} ${hoverPill}`}
      >
        <div
          className={`flex flex-col items-center ${transition} ${expanded ? 'gap-2' : 'gap-0'} group-hover:gap-2`}
        >
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)]" />
            <span
              className={`whitespace-nowrap ${transition} ${expanded ? 'text-sm' : 'text-xs'} font-semibold group-hover:text-sm`}
            >
              Client View
            </span>
            <ArrowRightLeft
              className={`shrink-0 ${transition} ${expanded ? 'h-3.5 w-3.5' : 'h-3 w-3'} group-hover:h-3.5 group-hover:w-3.5`}
            />
          </div>

          <div
            className={`flex items-center overflow-hidden ${transition} ${expanded ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0'} group-hover:max-h-10 group-hover:opacity-100`}
          >
            <Button
              size="sm"
              className="h-7 rounded-full bg-white/15 px-4 text-xs text-white hover:bg-white/25"
              onClick={e => {
                e.stopPropagation();
                toggleRole();
                setExpanded(false);
              }}
            >
              Switch to Advisor
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RouteProxy(route: AppRoute) {
  const params = useParams();
  const { allowedSessionTypes, pageFn: Component, path } = route;
  const isClientRoute = path.includes('/clients/:clientId');
  const isOnboardingRoute = isPublicOnboardingPath(path);

  if (allowedSessionTypes.length > 0) {
    if (isClientRoute) {
      return (
        <>
          {/*<AdminModeBanner />*/}
          <ClientViewMode />
          <ClientRoute
            component={Component}
            allowedSessionTypes={allowedSessionTypes}
          />
        </>
      );
    }
    if (isOnboardingRoute) {
      return (
        <>
          {/*<AdminModeBanner />*/}
          <ClientViewMode />
          <Component {...params} />
        </>
      );
    }
    return (
      <>
        {/*<AdminModeBanner />*/}
        <ClientViewMode />
        <ProtectedRoute
          component={Component}
          allowedSessionTypes={allowedSessionTypes}
        />
      </>
    );
  }
  return (
    <>
      {/*<AdminModeBanner />*/}
      <ClientViewMode />
      <Component {...params} />
    </>
  );
}

export function pageToUrl(page: PageFn<{}>): string;
export function pageToUrl(page: PageFn<{}>, targetProps: {} | undefined): string;
export function pageToUrl<TProps extends ObjectMap<string>>(
  page: PageFn<TProps>,
  props: TProps,
): string;

export function pageToUrl(page: PageFn<any>, props: ObjectMap<string> = {} as ObjectMap<string>) {
  const route =
    appRoutesByPage.get(page) ?? (rebuildAppRoutesByPage() && appRoutesByPage.get(page));
  if (route === undefined) {
    throw new ExError(
      'A development error occurred; attempted to link to a non-routed page. Add page to the path map in Router.',
      {
        targetPage: page.name,
        props,
        routedPages: Array.from(appRoutesByPage.entries()).map(([pageKey, { path }]) => ({
          pageKey: pageKey.name,
          path,
        })),
      },
    );
  }

  assertSameArrays(
    route.paramNames,
    Object.keys(props),
    'A development error occurred; attempted to link to a page with incorrect parameters.',
    {
      props,
      route,
    },
  );

  return route.path.replace(/\/:(\w+)/g, (_, propName) => `/${props[propName]}`);
}

export function useNavigateToPage() {
  const navigate = useNavigate();
  return navigateToPage;
  function navigateToPage(page: PageFn<{}>): void;
  function navigateToPage<TProps extends ObjectMap<string>>(
    page: PageFn<TProps>,
    props: TProps,
  ): void;
  function navigateToPage(page: PageFn, props: ObjectMap<string> = {} as ObjectMap<string>) {
    const url = pageToUrl(page, props);
    navigate(url);
  }
}

function rebuildAppRoutesByPage(): true {
  appRoutesByPage.clear();
  [
    ...appRoutes,
    ...genworthAdvisorOnlyAppRoutes,
    ...genworthAuthenticatedAppRoutes,
    ...genworthEducationAppRoutes,
  ].forEach(route => {
    const { pageFn } = route;
    if (!appRoutesByPage.has(pageFn)) {
      appRoutesByPage.set(pageFn, route);
    }
  });
  return true; // allows us to call as part of an expression..
}

function createAppRoute(pageFn: PageFn, path: string, allowedSessionTypes: SessionType[]) {
  return {
    pageFn,
    path,
    paramNames: regexExtractCaptures(path, /:(\w+)/g) ?? [],
    allowedSessionTypes,
  } as AppRoute;
}

export function getPublicRoutePaths(): string[] {
  return unauthenticatedAppRoutes
    .map(route => route.path)
    .filter(path => path !== '/')
    .map(path => {
      // Convert parameterized paths to base paths for prefix matching
      // e.g., '/public/seat-invitations/:id/:action' -> '/public/seat-invitations'
      const firstParamIndex = path.indexOf('/:');
      return firstParamIndex > 0 ? path.substring(0, firstParamIndex) : path;
    });
}
